import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import HeroBackdrop from "../components/HeroBackdrop";
import { Lightbox } from "../components/Lightbox";

const PRESET_CATS = ["Riders", "Drivers", "Training", "Events", "Social", "Awards", "General"];
const toEmbed = (u) => {
  const yt = (u || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = (u || "").match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return u;
};

export default function Gallery() {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const present = Array.from(new Set(items.map((i) => i.category || "General")));
  const ordered = [...PRESET_CATS.filter((c) => present.includes(c)), ...present.filter((c) => !PRESET_CATS.includes(c)).sort()];
  const CATS = ["All", ...ordered];

  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);

  return (
    <div data-testid="gallery-page">
      <section className="relative overflow-hidden bg-[#0F2744] py-16 lg:py-20">
        <HeroBackdrop sectionKey="gallery" image={t("img.gallery", "https://images.unsplash.com/photo-1506424482693-1f123321fa53?auto=format&fit=crop&q=85&w=1600")} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="eyebrow">Club life</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold text-white">Gallery</h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">Rides, training days and social events — a glimpse of club life.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filters">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                cat === c ? "bg-[#0F2744] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0F2744]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
            {filtered.map((it, i) => (
              <figure key={it.id} onClick={() => setOpen(i)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setOpen(i)}
                className="break-inside-avoid rounded-2xl overflow-hidden bg-white border border-slate-200 group cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-amber-500" data-testid={`gallery-item-${it.id}`}>
                <div className="overflow-hidden">
                  {it.type === "video"
                    ? (/(youtube|youtu\.be|vimeo)/.test(it.url)
                        ? <iframe src={toEmbed(it.url)} title={it.caption || "Video"} className="w-full aspect-video" allowFullScreen frameBorder="0" onClick={(e) => e.stopPropagation()} />
                        : <video src={it.url} controls className="w-full" onClick={(e) => e.stopPropagation()} />)
                    : <img src={it.url} alt={it.caption} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                {it.caption && <figcaption className="p-4 text-sm text-slate-600">{it.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
        {open !== null && <Lightbox items={filtered} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />}
      </div>
    </div>
  );
}
